import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import { FC, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { AbsolutCentered } from "../../AbsolutCentered/AbsolutCentered"
import { useClient } from "../../Client/ClientAndUserProvider"
import { GoogleTagManagerMode } from "../../Client/FeatureTypes"
import { PenIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import orderConfirmStyle from "../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import { SbtH4 } from "../../Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "../../Orders/Components/Text/SbtInvalid/SbtRHFError"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import consumerInfoStyle from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation.module.css"
import { onWheelRef } from "../CustomerPortalCustomerDetails/CustomerPortalCustomerDetails"
import myAccountStyle from "../CustomerPortalMyAccount/CustomerPortalMyAccount.module.css"
import style from "./GoogleTagManagerSettings.module.css"
import {
	GetGoogleTagManagerSettings,
	GetGoogleTagManagerSettingsResponse,
	SetGoogleTagManagerSettingsRequest,
	SetGoogleTagManagerSettingsResponse,
} from "./GoogleTagManagerSettingsModel"

type EditGoogleTagManagerSettingsProps = {
	settings: GetGoogleTagManagerSettings | null
	onClose: () => void
	onDone: (settings: GetGoogleTagManagerSettings | null) => void
}

const EditGoogleTagManagerSettingsFormSchema = z.object({
	tagManagerId: z.string().nullable(),
})

type EditGoogleTagManagerSettingsFormSchemaType = z.input<typeof EditGoogleTagManagerSettingsFormSchema>

const EditGoogleTagManagerSettings: FC<EditGoogleTagManagerSettingsProps> = (props: EditGoogleTagManagerSettingsProps) => {
	const client = useClient()
	const permissions = usePermissions()

	const [submitting, setSubmitting] = useState(false)

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isValid },
	} = useForm<EditGoogleTagManagerSettingsFormSchemaType>({
		resolver: async (data, context, options) => {
			return zodResolver(EditGoogleTagManagerSettingsFormSchema)(data, context, options)
		},
		defaultValues: {
			tagManagerId: props?.settings?.tagManagerId,
		},
	})

	async function onSubmit(data: EditGoogleTagManagerSettingsFormSchemaType) {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update)) {
			return
		}

		setSubmitting(true)
		let req: SetGoogleTagManagerSettingsRequest
		if (!data.tagManagerId) {
			req = {
				googleTagManagerSettings: null,
			}
		} else {
			req = {
				googleTagManagerSettings: {
					tagManagerId: data.tagManagerId.trim(),
				},
			}
		}

		API.postWithRetries<SetGoogleTagManagerSettingsResponse, SetGoogleTagManagerSettingsRequest>(
			`/customer-portal/google-tag-manager-settings-v1/${client.identifier}`,
			req,
		)
			.then((res) => {
				props.onDone(res.googleTagManagerSettings || null)
			})
			.catch(() => {})
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update)) {
		return null
	}

	return (
		<ModulePopup className={style.modal} onClose={props.onClose} disableCloseWithEsc={submitting}>
			<div className={style.wrapper}>
				<div
					className={consumerInfoStyle.headerText}
					style={{ marginTop: "-15px", marginBottom: "35px", marginRight: "30px" }}>
					Redigera Google Tag Manager-inställningar
				</div>
				<form className={style.form} onSubmit={handleSubmit((data) => onSubmit(data))} autoComplete="off">
					<div className={orderConfirmStyle.fields}>
						<label>
							<SbtH4>GTM-ID</SbtH4>
							<input
								className={orderConfirmStyle.input}
								disabled={submitting}
								{...register("tagManagerId")}
								placeholder="GTM-XXXXXXXXX"
							/>
							<SbtRHFError error={errors.tagManagerId} />
						</label>
					</div>
					<FinalizeButton className={style.finalize} disabled={submitting || !isDirty || !isValid} type="submit">
						Spara inställningar{" "}
						{submitting ? <FontAwesomeIcon style={{ color: "gray" }} spin={true} icon={faSpinner} /> : null}
					</FinalizeButton>
				</form>
			</div>
		</ModulePopup>
	)
}

export const GoogleTagManagerSettings: FC = () => {
	const client = useClient()
	const permissions = usePermissions()

	const [googleTagManagerSettings, setGoogleTagManagerSettings] = useState<
		GetGoogleTagManagerSettings | "None" | null | "Loading"
	>("Loading")
	const [edit, setEdit] = useState<boolean>(false)

	function getSettings() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)) {
			return
		}

		API.getWithRetries<GetGoogleTagManagerSettingsResponse>(
			`/customer-portal/google-tag-manager-settings-v1/${client.identifier}`,
			true,
		)
			.then((response) => {
				setGoogleTagManagerSettings(response?.googleTagManagerSettings ?? "None")
			})
			.catch(() => {
				setGoogleTagManagerSettings(null)
			})
	}

	useEffect(() => {
		if (
			client.features.googleTagManagerMode === GoogleTagManagerMode.Disabled ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)
		) {
			return
		}
		getSettings()
	}, [client.features.googleTagManagerMode, permissions])

	if (
		client.features.googleTagManagerMode === GoogleTagManagerMode.Disabled ||
		!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)
	) {
		return null
	}

	if (googleTagManagerSettings === null) {
		return null
	}

	if (googleTagManagerSettings === "Loading") {
		return (
			<div className={consumerInfoStyle.wrapper} style={{ minHeight: "250px" }}>
				<AbsolutCentered>
					<Loader />
				</AbsolutCentered>
			</div>
		)
	}

	return (
		<>
			{permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update) && edit ? (
				<EditGoogleTagManagerSettings
					settings={googleTagManagerSettings !== "None" ? googleTagManagerSettings : null}
					onDone={() => {
						getSettings()
						setEdit(false)
					}}
					onClose={() => {
						setEdit(false)
					}}
				/>
			) : null}
			<div className={consumerInfoStyle.wrapper}>
				<div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
					<div className={consumerInfoStyle.headerText}>Google Tag Manager-inställningar</div>
					{permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update) ? (
						<div>
							<PenIcon
								size={22}
								iconClassName={myAccountStyle.editIcon}
								onClick={() => {
									setEdit(true)
								}}
							/>
						</div>
					) : null}
				</div>
				<div style={{ marginTop: "35px" }}>
					<div className={consumerInfoStyle.textInputWithLabel}>
						<label>GTM-ID</label>
						<span
							ref={onWheelRef}
							className={consumerInfoStyle.textAsInput}
							title={googleTagManagerSettings !== "None" ? googleTagManagerSettings.tagManagerId : undefined}>
							{googleTagManagerSettings !== "None" ? googleTagManagerSettings.tagManagerId : null}
						</span>
					</div>
				</div>
			</div>
		</>
	)
}
